"use client";
import { type JSX, type ReactNode, useEffect, useState } from "react";
import { Button, ButtonProps } from "@cappelendamm/design-system/react";
import { useCart } from "@/cart";
import classNames from "classnames";

import { RefreshDouble } from "iconoir-react";
import { isItemDigital } from "@/products/selectors/isItemDigital";
import { getAddToCartButtonState } from "@/utils/getAddToCartButtonState";
import { useFeidePurchaseGate } from "@/products/components/useFeidePurchaseGate";

export type AddToCartButtonProps = {
  sku: string;
  canBeSold: boolean;
  statusMessage?: string;
  publicationDate?: string;
  iconOnly?: boolean;
  /**
   * Overrides the presentation when the item is a long-horizon preorder.
   * Lets the caller configure the label/icon (`children`) as well as the
   * hover text (`title`) and `aria-label` shown for preorderable items.
   * When omitted, the default "Forhåndsbestill" label is used.
   */
  preorder?: {
    children?: ReactNode;
    "aria-label"?: string;
    title?: string;
  };
  requiresFeideUser?: boolean;
};

export function AddToCartButton({
  children = "Legg i handlekurv",
  onClick,
  sku,
  className,
  canBeSold,
  statusMessage,
  publicationDate,
  iconOnly = false,
  preorder,
  "aria-label": ariaLabel,
  title,
  requiresFeideUser = false,
  ...rest
}: AddToCartButtonProps & ButtonProps & JSX.IntrinsicElements["button"]) {
  const [isSubmittingCurrentButton, setIsSubmittingCurrentButton] =
    useState(false);

  const { addItem, isPending, cart, cartDisabled } = useCart();
  const { isFeidePurchaseBlocked, isFeideAuthLoading } =
    useFeidePurchaseGate(requiresFeideUser);

  const currentItem = cart?.items?.find((item) => item.variant?.sku === sku);

  const isDigital = isItemDigital(currentItem);

  const {
    disabled: isUnavailable,
    label: disabledLabel,
    isLongHorizonPreorder: isPreorder,
  } = getAddToCartButtonState(canBeSold, statusMessage, publicationDate);
  const disabled = isUnavailable || isFeidePurchaseBlocked;

  const isCurrentButtonPending = isSubmittingCurrentButton;
  const isButtonDisabled =
    isPending ||
    isCurrentButtonPending ||
    disabled ||
    isDigital ||
    isFeideAuthLoading;

  const usePreorderOverride = Boolean(isPreorder) && preorder !== undefined;
  const resolvedAriaLabel =
    usePreorderOverride && preorder?.["aria-label"] !== undefined
      ? preorder["aria-label"]
      : ariaLabel;
  const resolvedTitle =
    usePreorderOverride && preorder?.title !== undefined
      ? preorder.title
      : title;

  let content: ReactNode;
  if (disabled && !iconOnly) {
    content = disabledLabel ?? "Ikke tilgjengelig";
  } else if (iconOnly) {
    content = children;
  } else if (usePreorderOverride && preorder?.children !== undefined) {
    content = preorder.children;
  } else {
    content = disabledLabel ?? children;
  }

  useEffect(() => {
    if (!isPending && isSubmittingCurrentButton) {
      setIsSubmittingCurrentButton(false);
    }
  }, [isPending, isSubmittingCurrentButton]);

  if (cartDisabled) return null;

  return (
    <Button
      {...rest}
      aria-label={resolvedAriaLabel}
      title={resolvedTitle}
      className={classNames(className, "relative", {
        "whitespace-nowrap": !iconOnly,
        "disabled:bg-(--cvr-button-background) disabled:text-(--cvr-button-contrast)":
          Boolean(cart) && isCurrentButtonPending,
        "cursor-not-allowed": Boolean(disabled) && !isPending,
      })}
      disabled={isButtonDisabled || undefined}
      aria-busy={isCurrentButtonPending || undefined}
      onClick={async (event) => {
        if (isButtonDisabled) {
          event.preventDefault();
          return;
        }
        setIsSubmittingCurrentButton(true);
        try {
          await addItem({ sku });
        } catch {
          setIsSubmittingCurrentButton(false);
        }
        if (onClick) onClick(event);
      }}
    >
      <span
        className={classNames("inline-flex items-center gap-1", {
          "opacity-0": isCurrentButtonPending && Boolean(cart),
        })}
      >
        {content}
      </span>
      {isCurrentButtonPending && Boolean(cart) && (
        <RefreshDouble
          width={14}
          height={14}
          className="cvr-icon--stroke pointer-events-none absolute inset-0 m-auto animate-spin"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        />
      )}
    </Button>
  );
}
